import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, PhoneIncoming, PhoneMissed, PhoneOutgoing, Play, Voicemail } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTelephonyStore } from '../../store/telephonyStore';
import type { Call } from '../../types/telephony';

function isVoicemail(c: Call): boolean {
  return c.direction === 'inbound' && c.status === 'no_answer' && !!c.recording_storage_path;
}

function callLabel(c: Call): string {
  if (isVoicemail(c)) return 'Messagerie vocale';
  if (c.status === 'no_answer') return 'Appel manqué';
  if (c.status === 'busy') return 'Occupé';
  if (c.status === 'failed') return 'Échec';
  if (c.status === 'canceled') return 'Annulé';
  return c.direction === 'inbound' ? 'Appel reçu' : 'Appel émis';
}

function CallIcon({ c }: { c: Call }) {
  if (isVoicemail(c)) return <Voicemail className="h-4 w-4 text-amber-500" />;
  if (c.status === 'no_answer' || c.status === 'busy' || c.status === 'failed') {
    return <PhoneMissed className="h-4 w-4 text-red-500" />;
  }
  return c.direction === 'inbound'
    ? <PhoneIncoming className="h-4 w-4 text-emerald-500" />
    : <PhoneOutgoing className="h-4 w-4 text-blue-500" />;
}

function fmtDuration(s: number | null): string {
  if (!s) return '';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m > 0 ? `${m} min ${sec}s` : `${sec}s`;
}

/** Call & voicemail history for a contact: status, transcript, audio playback. */
export function CallHistory({ contactId }: { contactId: string }) {
  const { fetchContactComms, getRecordingUrl } = useTelephonyStore();
  const [calls, setCalls] = useState<Call[]>([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);
  const [audioUrls, setAudioUrls] = useState<Record<string, string>>({});
  const [loadingAudio, setLoadingAudio] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { calls } = await fetchContactComms(contactId);
    setCalls(calls);
    setLoading(false);
  }, [contactId, fetchContactComms]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadAudio = async (c: Call) => {
    if (audioUrls[c.id] || loadingAudio) return;
    setLoadingAudio(c.id);
    const url = await getRecordingUrl(c.id);
    if (url) setAudioUrls((prev) => ({ ...prev, [c.id]: url }));
    setLoadingAudio(null);
  };

  if (loading) return <p className="text-xs text-neutral-400">Chargement…</p>;
  if (calls.length === 0) return <p className="text-xs text-neutral-400">Aucun appel.</p>;

  return (
    <ul className="space-y-1.5">
      {calls.map((c) => {
        const open = openId === c.id;
        const hasRecording = !!c.recording_storage_path;
        return (
          <li key={c.id} className="rounded-lg border border-neutral-200 dark:border-gray-800">
            <button
              type="button"
              onClick={() => setOpenId(open ? null : c.id)}
              className="flex w-full items-center gap-2 px-2.5 py-2 text-left"
            >
              <CallIcon c={c} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-neutral-800 dark:text-gray-100">{callLabel(c)}</span>
                  {c.duration_s ? <span className="text-xs text-neutral-400">{fmtDuration(c.duration_s)}</span> : null}
                </div>
                <span className="text-xs text-neutral-400">
                  {formatDistanceToNow(new Date(c.created_at), { addSuffix: true, locale: fr })}
                </span>
              </div>
              {(c.transcript || hasRecording) && (open ? <ChevronDown className="h-4 w-4 text-neutral-400" /> : <ChevronRight className="h-4 w-4 text-neutral-400" />)}
            </button>

            {open && (
              <div className="space-y-2 border-t border-neutral-100 px-2.5 py-2 dark:border-gray-800">
                {hasRecording && (
                  audioUrls[c.id] ? (
                    <audio controls src={audioUrls[c.id]} className="w-full" />
                  ) : (
                    <button
                      type="button"
                      onClick={() => loadAudio(c)}
                      disabled={loadingAudio === c.id}
                      className="inline-flex items-center gap-1.5 rounded-md bg-neutral-100 px-2.5 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-200 disabled:opacity-50 dark:bg-gray-800 dark:text-gray-200"
                    >
                      <Play className="h-3.5 w-3.5" />
                      {loadingAudio === c.id ? 'Chargement…' : 'Écouter l\'enregistrement'}
                    </button>
                  )
                )}
                {c.transcript ? (
                  <p className="whitespace-pre-wrap text-xs leading-relaxed text-neutral-600 dark:text-gray-300" data-sensitive-text="">{c.transcript}</p>
                ) : (
                  <p className="text-xs text-neutral-400">Pas de transcription.</p>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
